import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col, Form, Card, Button, Spinner } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { BsFunnelFill, BsSearch, BsArrowClockwise } from 'react-icons/bs';
import { LuGraduationCap } from 'react-icons/lu';
import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useSidebar } from './SidebarContext';
import ProfileDropdown from './commonComponents/ProfileDropdown';
import { useInstitute } from './commonComponents/InstituteContext';
import { useNavigate } from 'react-router-dom';

const GenerateTC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isCollapsed } = useSidebar();
  const [marginLeft, setMarginLeft] = useState('0px');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [logoError, setLogoError] = useState(false);
  const { logo, name, id } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(true);

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedCourseName, setSelectedCourseName] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [data, setData] = useState([]);

  const [selectedInstituteId, setSelectedInstituteId] = useState(() => {
    const cookieInstituteId = Cookies.get('institute_id');
    if (cookieInstituteId) return Number(cookieInstituteId);
    return id !== undefined && id !== null && id !== '' ? Number(id) : '';
  });

  const getNowDatetimeLocalValue = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = now.getFullYear();
    const mm = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const min = pad(now.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const formatDatetimeLocalToApi = (value) => {
    // value comes from <input type="datetime-local" />: "YYYY-MM-DDTHH:mm"
    // API docs show: "YYYY-MM-DD HH:mm:ss"
    if (!value) return null;
    const [datePart, timePart] = value.split('T');
    if (!datePart || !timePart) return null;
    return `${datePart} ${timePart}:00`;
  };

  const formatDateToApi = (value) => {
    // value comes from <input type="date" />: "YYYY-MM-DD"
    if (!value) return null;
    return `${value} 00:00:00`;
  };

  const [tcSubmitting, setTcSubmitting] = useState(false);
  const [tcFormErrors, setTcFormErrors] = useState({});
  const [tcFormData, setTcFormData] = useState({
    tc_issue_date: getNowDatetimeLocalValue(),
    course_completion_date: '',
    reason: '',
    remarks: '',
  });

  const selectedStudentObj = useMemo(() => {
    if (!selectedStudent) return null;
    return students.find((s) => Number(s.id) === Number(selectedStudent)) || null;
  }, [students, selectedStudent]);

  const selectedCourseObj = useMemo(() => {
    if (!selectedCourseId) return null;
    return courses.find((c) => Number(c.id) === Number(selectedCourseId)) || null;
  }, [courses, selectedCourseId]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245;
        const collapsedWidth = 85;
        const gap = 1;
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`
        );
      } else {
        setMarginLeft('0px');
      }
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  useEffect(() => {
    if (id === undefined || id === null || id === '') return;
    const nextInstituteId = Number(id);
    if (Number.isNaN(nextInstituteId)) return;
    if (String(selectedInstituteId) !== String(nextInstituteId)) {
      setSelectedInstituteId(nextInstituteId);
      Cookies.set('institute_id', nextInstituteId, { expires: 7 });
    }
  }, [id, selectedInstituteId]);

  useEffect(() => {
    if (!selectedInstituteId) return;
    const cookieInstituteId = Cookies.get('institute_id');
    if (cookieInstituteId !== String(selectedInstituteId)) {
      Cookies.set('institute_id', selectedInstituteId, { expires: 7 });
    }

    ApiEndpoints.getAllCourses()
      .then((res) => setCourses(res.data.data || []))
      .catch(console.error);
  }, [selectedInstituteId]);

  useEffect(() => {
    if (selectedCourseId) {
      ApiEndpoints.getAllClassesByCourseId(selectedCourseId)
        .then((res) => {
          const { data } = res;
          if (data.status === false || !data.data?.length) {
            setClasses([]);
          } else {
            setClasses(data.data);
          }
        })
        .catch(() => setClasses([]));
    } else {
      setClasses([]);
    }
  }, [selectedCourseId]);

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setSelectedCourseId(courseId);
    if (courseId) {
      const courseName = courses.find((c) => c.id === Number(courseId));
      setSelectedCourseName(courseName);
      ApiEndpoints.getAllClassesByCourseId(courseId).then((res) => {
        setClasses(res.data.data || []);
      });
    } else {
      setSelectedCourseName('');
      setClasses([]);
      setSelectedClass('');
      setStudents([]);
      setSelectedStudent('');
    }
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    if (classId) {
      const userCookie = Cookies.get('user')
      let token = null
      if (userCookie) {
        try {
          token = JSON.parse(userCookie)?.token || null
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }
      const headers = {
        'institute-Id': Number(selectedInstituteId || Cookies.get('institute_id')),
        Authorization: token ? `Bearer ${token}` : undefined,
      };
      axios
        .get(BASE_URL + `/get/student/by-class?class_id=${classId}`, { headers })
        .then((res) => {
          setData(res.data.data || []);
          setStudents(res.data.data || []);
        })
        .catch(() => {
          setData([]);
          setStudents([]);
        });
    } else {
      setData([]);
      setStudents([]);
      setSelectedStudent('');
    }
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    setSelectedStudent(studentId);
  };

  const handleRefresh = () => {
    setSelectedCourseId('');
    setSelectedClass('');
    setSelectedStudent('');
    setData([]);
    setStudents([]);
  };

  const validateTcForm = () => {
    const errors = {};

    if (!selectedInstituteId) errors.institute_id = 'Institute is required';
    if (!selectedStudent) errors.student_id = 'Student is required';
    if (!selectedCourseId) errors.course_id = 'Course is required';

    const reason = tcFormData.reason?.trim() || '';
    if (reason.length > 255) {
      errors.reason = 'Reason cannot exceed 255 characters';
    }

    if (tcFormData.course_completion_date) {
      // Basic sanity check for date input format.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(tcFormData.course_completion_date)) {
        errors.course_completion_date = 'Invalid completion date';
      }
    }

    return errors;
  };

  const handleSubmitTc = async (e) => {
    e.preventDefault();

    const errors = validateTcForm();
    setTcFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setTcSubmitting(true);
      setTcFormErrors({});

      const userCookie = Cookies.get('user');
      const token = userCookie ? JSON.parse(userCookie)?.token : null;
      if (!token) {
        toast.error('Authorization token missing. Please login again.');
        return;
      }

      const payload = {
        institute_id: Number(selectedInstituteId),
        student_id: Number(selectedStudent),
        course_id: Number(selectedCourseId),
        tc_issue_date:
          formatDatetimeLocalToApi(tcFormData.tc_issue_date) ||
          formatDatetimeLocalToApi(getNowDatetimeLocalValue()),
      };

      if (tcFormData.course_completion_date) {
        payload.course_completion_date = formatDateToApi(
          tcFormData.course_completion_date,
        );
      }

      const reason = tcFormData.reason?.trim();
      if (reason) payload.reason = reason;

      const remarks = tcFormData.remarks?.trim();
      if (remarks) payload.remarks = remarks;

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'institute-Id': Number(selectedInstituteId),
      };

      const response = await axios.post(
        `${BASE_URL}/transfer-certificates`,
        payload,
        { headers },
      );

      toast.success(
        response?.data?.message || 'Transfer Certificate issued successfully',
      );

      // Reset only the form fields (keep selections for faster re-issue).
      setTcFormData({
        tc_issue_date: getNowDatetimeLocalValue(),
        course_completion_date: '',
        reason: '',
        remarks: '',
      });
      setTcFormErrors({});
    } catch (error) {
      const errorData = error.response?.data || {};

      // Laravel validation errors often come as { errors: { field: [msg] } }
      if (errorData?.errors && typeof errorData.errors === 'object') {
        const mapped = {};
        Object.entries(errorData.errors).forEach(([field, msgs]) => {
          mapped[field] = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
        });
        setTcFormErrors(mapped);
      }

      const msg =
        errorData?.message ||
        errorData?.result ||
        'Failed to issue Transfer Certificate';
      toast.error(msg);
    } finally {
      setTcSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }
          .rbt-menu {
            z-index: 1051 !important;
          }

          .tc-form-anim {
            transition: max-height 0.35s ease, opacity 0.25s ease, transform 0.25s ease;
            overflow: hidden;
            will-change: max-height, opacity, transform;
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0 no-print"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  TC Generate
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        {/* Non-printable controls */}
        <div className="no-print">
          <div
            className="container-fluid p-0 mt-4"
            style={{
              paddingLeft: isCollapsed ? '32px' : '40px',
              paddingRight: '32px',
              paddingTop: '32px',
              paddingBottom: '32px',
            }}
          >
            {/* Filter Toggle Button */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <button
                  onClick={() => setShowFilterCard(!showFilterCard)}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                    border: '1px solid #D1D3D4',
                    color: showFilterCard ? '#ffffff' : '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843';
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff';
                    e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843';
                    e.currentTarget.style.borderColor = '#D1D3D4';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
                >
                  <BsFunnelFill size={20} />
                </button>
                {/* <Button
                  onClick={() => navigate('/TemplateAssignment')}
                  style={{
                    borderRadius: '10px',
                    padding: '8px 14px',
                    height: '44px',
                    border: 'none',
                    backgroundColor: '#223843',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Manage TC Templates
                </Button> */}
              </div>
            </div>

            {/* Filter Section */}
            <div
              className="filter-card-container"
              style={{
                maxHeight: showFilterCard ? '1000px' : '0',
                overflow: showFilterCard ? 'visible' : 'hidden',
                transition: 'max-height 0.3s ease-in-out',
                marginBottom: showFilterCard ? '24px' : '0',
                position: 'relative',
                zIndex: showFilterCard ? 10 : 1,
              }}
            >
              {showFilterCard && (
                <>
                  <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    overflow: 'visible',
                    position: 'relative',
                  }}
                  className="filter-card-container"
                >
                  <div
                    style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                        </div>
                        <div>
                          <h5
                            className="m-0 fw-semibold text-start"
                            style={{
                              color: '#111827',
                              fontSize: '16px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Search & Filter TC
                          </h5>
                          <p className="m-0 mt-1 text-start" style={{ fontSize: '13px', color: '#6b7280' }}>
                            Select course, class, and student to generate Transfer Certificate
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>

                  <Card.Body
                    className="p-4 text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      overflow: 'visible',
                      position: 'relative',
                    }}
                  >
                    <Row className="g-4 text-start">
                      <Col xs={12} md={4}>
                        <div style={{ position: 'relative' }}>
                          <Form.Group className="text-start fw-bold mb-0">
                            <Form.Label
                              style={{
                                fontSize: '13px',
                                color: '#6b7280',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                marginBottom: '8px',
                              }}
                            >
                              Select Course
                            </Form.Label>
                            <Typeahead
                              id="tc-course-select"
                              labelKey="name"
                              onChange={(selected) => {
                                const course = selected[0];
                                if (course) {
                                  handleCourseChange({ target: { value: course.id } });
                                } else {
                                  handleCourseChange({ target: { value: '' } });
                                }
                              }}
                              options={courses}
                              placeholder="Select Course"
                              selected={courses.filter(
                                (cs) => cs.id === Number(selectedCourseId || '')
                              )}
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col xs={12} md={4}>
                        <div style={{ position: 'relative' }}>
                          <Form.Group className="text-start fw-bold mb-0">
                            <Form.Label
                              style={{
                                fontSize: '13px',
                                color: '#6b7280',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                marginBottom: '8px',
                              }}
                            >
                              Select Class
                            </Form.Label>
                            <Typeahead
                              id="tc-class-select"
                              labelKey="class_name"
                              onChange={(selected) => {
                                const cls = selected[0];
                                if (cls) {
                                  handleClassChange({ target: { value: cls.id } });
                                } else {
                                  handleClassChange({ target: { value: '' } });
                                }
                              }}
                              options={classes}
                              placeholder="Select Class"
                              selected={classes.filter(
                                (cs) => cs.id === Number(selectedClass || '')
                              )}
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col xs={12} md={4}>
                        <div style={{ position: 'relative' }}>
                          <Form.Group className="text-start fw-bold">
                            <Form.Label
                              style={{
                                fontSize: '13px',
                                color: '#6b7280',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                marginBottom: '8px',
                              }}
                            >
                              Select Student
                            </Form.Label>
                            <Typeahead
                              id="tc-student-select"
                              labelKey={(option) =>
                                `${option.name} - ${option.admission_number}`
                              }
                              onChange={(selected) => {
                                const student = selected[0];
                                if (student) {
                                  handleStudentChange({ target: { value: student.id } });
                                } else {
                                  handleStudentChange({ target: { value: '' } });
                                }
                              }}
                              options={students}
                              placeholder="Select Student"
                              selected={
                                Array.isArray(students)
                                  ? students.filter((s) => s.id === Number(selectedStudent || ''))
                                  : []
                              }
                            />
                          </Form.Group>
                        </div>
                      </Col>
                    </Row>

                  </Card.Body>
                </Card>

                    <div
                      className="tc-form-anim"
                      style={{
                        maxHeight: selectedStudent ? '900px' : '0px',
                        opacity: selectedStudent ? 1 : 0,
                        transform: selectedStudent ? 'translateY(0px)' : 'translateY(-6px)',
                        pointerEvents: selectedStudent ? 'auto' : 'none',
                        marginTop: selectedStudent ? '20px' : '0px',
                      }}
                    >
                      {selectedStudent && (
                        <Card
                          style={{
                            border: '1px solid #D1D3D4',
                            borderRadius: '12px',
                            backgroundColor: '#ffffff',
                            overflow: 'visible',
                          }}
                        >
                          <div
                            style={{
                              background: '#223843',
                              color: '#ffffff',
                              padding: '14px 18px',
                              borderTopLeftRadius: '12px',
                              borderTopRightRadius: '12px',
                            }}
                          >
                            <h5 className="m-0" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                              Generate Transfer Certificate
                            </h5>
                            <p className="m-0 mt-1" style={{ fontSize: '12px', color: '#cbd5e1' }}>
                              Fill details and submit.
                            </p>
                          </div>

                          <Card.Body style={{ padding: '18px' }}>
                            <Form className="text-start" onSubmit={handleSubmitTc}>
                              <Row className="g-3">
                                <Col xs={12} md={6}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      Institute *
                                    </Form.Label>
                                    <Form.Control
                                      value={Cookies.get('name') || name || ''}
                                      disabled
                                      isInvalid={Boolean(tcFormErrors.institute_id)}
                                    />
                                    {tcFormErrors.institute_id && (
                                      <Form.Text className="text-danger">
                                        {tcFormErrors.institute_id}
                                      </Form.Text>
                                    )}
                                  </Form.Group>
                                </Col>

                                <Col xs={12} md={6}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      Course *
                                    </Form.Label>
                                    <Form.Control value={selectedCourseObj?.name || selectedCourseId || ''} disabled />
                                    {tcFormErrors.course_id && (
                                      <Form.Text className="text-danger">
                                        {tcFormErrors.course_id}
                                      </Form.Text>
                                    )}
                                  </Form.Group>
                                </Col>

                                <Col xs={12} md={6}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      Student *
                                    </Form.Label>
                                    <Form.Control
                                      value={selectedStudentObj?.name || ''}
                                      disabled
                                    />
                                    {tcFormErrors.student_id && (
                                      <Form.Text className="text-danger">
                                        {tcFormErrors.student_id}
                                      </Form.Text>
                                    )}
                                  </Form.Group>
                                </Col>

                                <Col xs={12} md={6}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      TC Issue Date
                                    </Form.Label>
                                    <Form.Control
                                      type="datetime-local"
                                      value={tcFormData.tc_issue_date}
                                      onChange={(e) =>
                                        setTcFormData((prev) => ({
                                          ...prev,
                                          tc_issue_date: e.target.value,
                                        }))
                                      }
                                    />
                                  </Form.Group>
                                </Col>

                                <Col xs={12} md={6}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      Course Completion Date
                                    </Form.Label>
                                    <Form.Control
                                      type="date"
                                      value={tcFormData.course_completion_date}
                                      onChange={(e) =>
                                        setTcFormData((prev) => ({
                                          ...prev,
                                          course_completion_date: e.target.value,
                                        }))
                                      }
                                      isInvalid={Boolean(tcFormErrors.course_completion_date)}
                                    />
                                    {tcFormErrors.course_completion_date && (
                                      <Form.Text className="text-danger">
                                        {tcFormErrors.course_completion_date}
                                      </Form.Text>
                                    )}
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                      Optional unless your backend requires it.
                                    </div>
                                  </Form.Group>
                                </Col>

                                <Col xs={12} md={6}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      Reason (max 255)
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={tcFormData.reason}
                                      onChange={(e) =>
                                        setTcFormData((prev) => ({
                                          ...prev,
                                          reason: e.target.value,
                                        }))
                                      }
                                      maxLength={255}
                                      isInvalid={Boolean(tcFormErrors.reason)}
                                    />
                                    {tcFormErrors.reason && (
                                      <Form.Text className="text-danger">
                                        {tcFormErrors.reason}
                                      </Form.Text>
                                    )}
                                  </Form.Group>
                                </Col>

                                <Col xs={12}>
                                  <Form.Group>
                                    <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>
                                      Remarks
                                    </Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={tcFormData.remarks}
                                      onChange={(e) =>
                                        setTcFormData((prev) => ({
                                          ...prev,
                                          remarks: e.target.value,
                                        }))
                                      }
                                      isInvalid={Boolean(tcFormErrors.remarks)}
                                    />
                                    {tcFormErrors.remarks && (
                                      <Form.Text className="text-danger">
                                        {tcFormErrors.remarks}
                                      </Form.Text>
                                    )}
                                  </Form.Group>
                                </Col>

                                {selectedStudentObj && (
                                  <Col xs={12}>
                                    <div
                                      style={{
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '10px',
                                        padding: '14px',
                                        background: '#F9FAFB',
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-3">
                                        <div
                                          style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            background: '#E5E7EB',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {selectedStudentObj.student_image ? (
                                            <img
                                              src={selectedStudentObj.student_image}
                                              alt="Student"
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                              }}
                                            />
                                          ) : null}
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{ fontWeight: 700, color: '#111827' }}>
                                            {selectedStudentObj.name}
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            Admission No: {selectedStudentObj.admission_number}
                                          </div>
                                          {(selectedStudentObj.father_name ||
                                            selectedStudentObj.mother_name) && (
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                              Father: {selectedStudentObj.father_name || '-'} | Mother:{' '}
                                              {selectedStudentObj.mother_name || '-'}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                )}

                                <Col xs={12} className="d-flex justify-content-end mt-2">
                                  <Button
                                    type="submit"
                                    disabled={tcSubmitting}
                                    style={{
                                      backgroundColor: '#223843',
                                      border: 'none',
                                      borderRadius: '10px',
                                      padding: '10px 16px',
                                      minWidth: '170px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '10px',
                                      opacity: tcSubmitting ? 0.8 : 1,
                                    }}
                                  >
                                    {tcSubmitting ? (
                                      <>
                                        <Spinner animation="border" size="sm" variant="light" />
                                        Issuing...
                                      </>
                                    ) : (
                                      'Issue TC'
                                    )}
                                  </Button>
                                </Col>
                              </Row>
                            </Form>
                          </Card.Body>
                        </Card>
                      )}
                    </div>
                  </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenerateTC;
